import React, { useEffect, useRef, useState } from 'react';

import { useMetrics } from '../../../hooks/useMetrics';
import { AUTH_EVENTS, AuthFunnelMode } from '../../../utils/authFunnel';
import {
  CenterWrap,
  CodeBoxes,
  ConfirmActions,
  LinkBtn,
  OAuthBtn,
  PrimaryBtn,
  Sticker,
  SupportRef,
} from './authStyles';
import { AuthStrings } from './useAuthStrings';

type ResendResult = { status: 'ok' | 'rate-limited' | 'error'; correlationId?: string };

type Props = {
  t: AuthStrings;
  email: string;
  /** login|signup, for funnel events. */
  mode: AuthFunnelMode;
  /** Verify the 6-digit code with the backend. Resolves on success. */
  onVerify: (code: string) => Promise<void>;
  /** Re-request a code for the same email. Resolves with the outcome. */
  onResend: () => Promise<ResendResult>;
  /** Switch to a different provider (back to the options screen). */
  onTryAnother: () => void;
  /** Re-enter a different email. */
  onChangeEmail: () => void;
};

/**
 * Inline magic-link second screen: paste/type the 6-digit code emailed to the
 * user. Verification is delegated to `onVerify` (backend-owned, CURI-334).
 */
const MagicLinkCodeConfirm: React.FC<Props> = ({
  t,
  email,
  mode,
  onVerify,
  onResend,
  onTryAnother,
  onChangeEmail,
}) => {
  const metrics = useMetrics(true);
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [verifying, setVerifying] = useState(false);
  const [resent, setResent] = useState(false);
  const [err, setErr] = useState('');
  const [errRef, setErrRef] = useState<string | undefined>();
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const resendTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    refs.current[0]?.focus();
  }, []);

  // Clear the resend cooldown timer if the component unmounts.
  useEffect(() => () => clearTimeout(resendTimer.current), []);

  const resend = async () => {
    if (resent) return;
    setErr('');
    setErrRef(undefined);
    metrics.logEvent(AUTH_EVENTS.codeResent, { mode });
    // Optimistically disable to prevent double-clicks while the request is in flight.
    setResent(true);
    const { status, correlationId } = await onResend();
    if (status !== 'ok') {
      // Re-enable immediately and surface why no code was sent (+ support ref).
      setResent(false);
      metrics.logEvent(AUTH_EVENTS.sendFailed, { mode, reason: status, resend: true });
      setErr(status === 'rate-limited' ? t.errTooManyRequests : t.errSendCodeFailed);
      if (status === 'error') setErrRef(correlationId);
      return;
    }
    // Re-enable after a short cooldown so a missed/spam-filtered code is recoverable.
    resendTimer.current = setTimeout(() => setResent(false), 30000);
  };

  const set = (idx: number, val: string) => {
    // Codes are alphanumeric and forced uppercase.
    const v = val
      .replace(/[^a-zA-Z0-9]/g, '')
      .toUpperCase()
      .slice(-1);
    setDigits(d => {
      const n = [...d];
      n[idx] = v;
      return n;
    });
    if (v && idx < 5) refs.current[idx + 1]?.focus();
  };

  const onKey = (idx: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) refs.current[idx - 1]?.focus();
  };

  const onPaste = (e: React.ClipboardEvent) => {
    const txt = (e.clipboardData.getData('text') || '')
      .replace(/[^a-zA-Z0-9]/g, '')
      .toUpperCase()
      .slice(0, 6);
    if (!txt) return;
    e.preventDefault();
    const n = ['', '', '', '', '', ''];
    txt.split('').forEach((c, k) => (n[k] = c));
    setDigits(n);
    refs.current[Math.min(txt.length, 5)]?.focus();
  };

  const full = digits.every(d => d !== '');

  const submit = async () => {
    if (!full || verifying) return;
    setErr('');
    setErrRef(undefined);
    metrics.logEvent(AUTH_EVENTS.codeSubmitted, { mode });
    setVerifying(true);
    try {
      await onVerify(digits.join(''));
    } catch (e) {
      metrics.logEvent(AUTH_EVENTS.codeFailed, { mode });
      setErr(e instanceof Error ? e.message : t.errCodeInvalid);
      setVerifying(false);
    }
  };

  if (verifying && !err) {
    return (
      <CenterWrap>
        <Sticker
          src="/auth/stickers/rocket.svg"
          alt=""
          style={{ width: 104, height: 104 }}
        />
        <h2>{t.signingIn}</h2>
        <p>{t.signingInSub}</p>
      </CenterWrap>
    );
  }

  return (
    <CenterWrap>
      <Sticker src="/auth/stickers/love.svg" alt="" style={{ width: 104, height: 104 }} />
      <h2>{t.magicTitle}</h2>
      <p>
        {t.codeSub} <span className="email-em">{email}</span>.
      </p>
      <CodeBoxes onPaste={onPaste}>
        {digits.map((d, idx) => (
          <input
            key={idx}
            ref={el => (refs.current[idx] = el)}
            value={d}
            inputMode="text"
            autoComplete={idx === 0 ? 'one-time-code' : 'off'}
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            style={{ textTransform: 'uppercase' }}
            maxLength={1}
            className={d ? 'filled' : ''}
            onChange={e => set(idx, e.target.value)}
            onKeyDown={e => onKey(idx, e)}
            aria-label={`${t.codeCharLabel} ${idx + 1}`}
          />
        ))}
      </CodeBoxes>
      {err && <p style={{ color: '#D84C42', fontSize: 13, fontWeight: 700 }}>{err}</p>}
      {errRef && (
        <SupportRef>
          {t.errSupportRef}: <code>{errRef}</code>
        </SupportRef>
      )}
      <div
        style={{
          marginTop: 18,
          fontSize: 14,
          fontWeight: 700,
          color: 'rgba(40,47,51,.5)',
        }}
      >
        {t.didntGet}{' '}
        <button
          onClick={resend}
          disabled={resent}
          style={{
            color: resent ? 'rgba(40,47,51,.38)' : '#E58332',
            fontWeight: 800,
            textDecoration: resent ? 'none' : 'underline',
            textUnderlineOffset: 2,
            background: 'none',
            border: 'none',
            cursor: resent ? 'default' : 'pointer',
            fontSize: 'inherit',
            fontFamily: 'inherit',
          }}
        >
          {resent ? t.codeResent : t.sendNew}
        </button>
      </div>
      <PrimaryBtn style={{ marginTop: 24 }} disabled={!full} onClick={submit}>
        {t.continue}
      </PrimaryBtn>
      <ConfirmActions>
        <OAuthBtn onClick={onTryAnother}>{t.tryAnother}</OAuthBtn>
        <LinkBtn onClick={onChangeEmail}>← {t.useDiff}</LinkBtn>
      </ConfirmActions>
    </CenterWrap>
  );
};

export default MagicLinkCodeConfirm;
